import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import { getMockServices } from '../data/mockData';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/config';

const ServiceDetail = () => {
  const { t } = useTranslation();
  const { serviceId } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const fetchServiceDetails = async () => {
      try {
        setLoading(true);

        // Wait for i18n to be initialized
        if (!i18n.isInitialized) {
          setLoading(false);
          return;
        }

        // Get translated mock services
        const translatedMockServices = getMockServices(t);
        // Find service from translated mockServices by ID
        const foundService = translatedMockServices.find(s => s.id === serviceId);
        if (foundService) {
          setService(foundService);
        } else {
          setError('Service not found');
        }
      } catch (err) {
        setError('Failed to load service details');
      } finally {
        setLoading(false);
      }
    };

    fetchServiceDetails();
  }, [serviceId, t]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-xl text-gray-600">{t('serviceDetail.loading')}</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-xl text-red-600">{error || t('serviceDetail.notFound')}</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Breadcrumb */}
      <div className="bg-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-sm text-gray-600">
            <a href="/" className="text-blue-500 hover:underline">{t('serviceDetail.companyName')}</a> &gt; {service.title}
          </div>
        </div>
      </div>

      {/* Page Header */}
      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl font-bold text-gray-800">{service.title}</h1>
        </div>
      </div>

      {/* Service Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          {/* Service Image */}
          <div className="mb-8">
            <img
              src={service.image}
              alt={service.title}
              className={`w-full rounded-lg shadow-lg ${service.id === 'upholstery-cleaning' ? 'object-contain' : 'h-96 object-cover'}`}
            />
          </div>

          {/* Service Videos (if available) */}
          {service.videos && service.videos.length > 0 && (
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">{t('serviceDetail.seeServiceInAction')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {service.videos.map((videoUrl, index) => (
                  <div key={index} className="relative bg-white rounded-lg shadow-lg overflow-hidden">
                    <video
                      controls
                      className="w-full"
                      poster={service.videoPoster || service.image}
                      style={{ backgroundColor: 'white' }}
                    >
                      <source src={videoUrl} type="video/mp4" />
                      {t('serviceDetail.videoNotSupported')}
                    </video>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Service Description */}
          <div className="prose max-w-none mb-8">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              {service.description}
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              {service.longDescription}
            </p>
          </div>

          {/* Service Features */}
          {service.features && service.features.length > 0 && (
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">{t('serviceDetail.keyFeatures')}</h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {service.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="w-6 h-6 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 24/7 Badge for Emergency Services */}
          {service.is24_7 && (
            <div className="mb-8 p-4 bg-green-50 border-l-4 border-green-500 rounded">
              <p className="text-green-800 font-semibold text-lg">
                {t('serviceDetail.emergency24_7Badge')}
              </p>
            </div>
          )}

          {/* Call to Action */}
          <div className="bg-blue-500 text-white p-8 rounded-lg text-center">
            <h3 className="text-2xl font-bold mb-4">
              {t('serviceDetail.getFreeEstimateFor')} {service.title}
            </h3>
            <div className="space-y-4">
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-yellow-400 text-blue-900 font-bold px-8 py-3 rounded-full hover:bg-yellow-300 transition-colors mr-4"
              >
                {t('serviceDetail.getFreeEstimate')}
              </button>
              <div className="text-xl">
                {t('serviceDetail.callNow')} <span className="font-bold">(450) 687-9220</span> or <span className="font-bold">(514) 816-9220</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <Footer />
    </div>
  );
};

export default ServiceDetail;